import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import HeroSlider from '../components/home/HeroSlider';
import Reveal from '../components/common/Reveal';
import EditorialIntro from '../components/home/EditorialIntro';
import { getOrganizationSchema, getWebSiteSchema } from '../utils/seoSchemas';

// Below-the-fold sections are code-split so they don't ship with the initial bundle.
const CategoryBanners = lazy(() => import('../components/home/CategoryBanners'));
const SplitEditorial = lazy(() => import('../components/home/SplitEditorial'));
const FeaturedProducts = lazy(() => import('../components/home/FeaturedProducts'));
const BehindPieces = lazy(() => import('../components/home/BehindPieces'));
const QuoteSection = lazy(() => import('../components/home/QuoteSection'));
const BrandsGrid = lazy(() => import('../components/home/BrandsGrid'));
const NewsletterSection = lazy(() => import('../components/home/NewsletterSection'));

const SectionFallback = ({ height = '320px' }) => (
  <div className="container mx-auto px-4 py-12 md:py-16">
    <div
      className="bg-gray-100 rounded-lg animate-pulse"
      style={{ minHeight: height }}
      aria-hidden="true"
    />
  </div>
);

const Home = () => {
  const { t } = useTranslation();
  return (
    <>
      {/* No title prop — the homepage tab just shows the bare brand name
          via SEOHead's siteTitle fallback, with the tagline as the
          description instead of a redundant "Home | CimonaMía". */}
      <SEOHead
        description={t('hero.tagline')}
        image="/logo512.png"
        imageAlt="CimonaMía"
        url="/"
        type="website"
      />
      
      {/* Structured Data for Organization and Website */}
      <script type="application/ld+json">
        {JSON.stringify(getOrganizationSchema())}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(getWebSiteSchema())}
      </script>
      
      <div className="min-h-screen">
        {/* SEO H1 - Visually hidden but accessible to search engines */}
        <h1 className="sr-only">Cimonamia - Sustainable Fashion & Eco-Friendly Clothing</h1>
        
        {/* Above the fold - eager load for fast LCP */}
        <HeroSlider />

        {/* Editorial intro */}
        <Reveal>
          <EditorialIntro />
        </Reveal>

        {/* Below-the-fold: each section code-split + single GSAP tween per section.
            No extra IntersectionObserver wrapper — ScrollTrigger handles reveal gating
            and Suspense lets the bundle download lazily once React reaches the node. */}
        <Suspense fallback={<SectionFallback height="420px" />}>
          <Reveal>
            <CategoryBanners />
          </Reveal>
        </Suspense>

        <Suspense fallback={<SectionFallback height="700px" />}>
          <Reveal>
            <SplitEditorial />
          </Reveal>
        </Suspense>

        <Suspense fallback={<SectionFallback height="560px" />}>
          <Reveal>
            <FeaturedProducts />
          </Reveal>
        </Suspense>

        <Suspense fallback={<SectionFallback height="640px" />}>
          <Reveal>
            <BehindPieces />
          </Reveal>
        </Suspense>

        <Suspense fallback={<SectionFallback height="480px" />}>
          <Reveal>
            <QuoteSection />
          </Reveal>
        </Suspense>

        <Suspense fallback={<SectionFallback height="320px" />}>
          <Reveal>
            <NewsletterSection />
          </Reveal>
        </Suspense>

        <Suspense fallback={<SectionFallback height="320px" />}>
          <Reveal>
            <BrandsGrid />
          </Reveal>
        </Suspense>
      </div>
    </>
  );
};

export default Home;
